import { ReactNode } from 'react';
import { useRoadmap } from './RoadmapContext';
import { LoadingState, ErrorBanner } from '@/components/ds';

interface ViewLayoutProps {
    /** The view's display title (e.g. "Goal Board") */
    title: string;
    /** Toolbar buttons / controls rendered to the right of the title */
    actions?: ReactNode;
    /** The main view content */
    children: ReactNode;
    /** View-specific data loading state (separate from roadmap metadata loading) */
    isLoading?: boolean;
    /** Custom text shown while loading */
    loadingText?: string;
    /** View-specific error message */
    error?: string | null;
    /**
     * Enable full-height flex layout for views that need to fill the viewport
     * (e.g. Kanban boards, canvas views). When false (default) uses standard
     * padded-and-spaced block layout.
     */
    fullHeight?: boolean;
}

/**
 * ViewLayout — shared inner layout for all roadmap view pages.
 *
 * Wires into RoadmapContext so it can gate rendering until the roadmap
 * metadata is ready, then delegates view-specific loading/error handling
 * through props. Provides a standardised header (title + actions slot)
 * and two layout modes: standard scrollable vs full-height flex.
 */
export function ViewLayout({
    title,
    actions,
    children,
    isLoading,
    loadingText = 'Loading...',
    error,
    fullHeight = false,
}: ViewLayoutProps) {
    const { isLoading: roadmapLoading, error: roadmapError } = useRoadmap();

    // Show loading while roadmap metadata OR view-specific data is fetching
    if (roadmapLoading || isLoading) {
        return <LoadingState label={loadingText} />;
    }

    // Surface roadmap-level errors (e.g. 404, permissions) before view errors
    const displayError = roadmapError?.message ?? error;
    if (displayError) {
        return (
            <div className="p-6">
                <ErrorBanner message={displayError} />
            </div>
        );
    }

    const header = (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h2 className="text-xl md:text-2xl font-black uppercase tracking-tighter">
                {title}<span className="text-orange-500">.</span>
            </h2>
            {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
        </div>
    );

    if (fullHeight) {
        return (
            <div className="flex flex-col h-full p-4 md:p-6">
                <div className="mb-4">{header}</div>
                {children}
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
            {header}
            {children}
        </div>
    );
}
